import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const calculateBubblePosition = (
  value: number,
  min: number,
  max: number,
  inputWidth: number,
  bubbleWidth: number
) => {
  const percentage = (value - min) / (max - min);
  const minPosPx = 0;
  const maxPosPx = inputWidth - bubbleWidth;
  const currentPosPx = percentage * inputWidth - bubbleWidth / 2;
  const normalizedPosPx = Math.max(minPosPx, Math.min(maxPosPx, currentPosPx));
  return normalizedPosPx;
};

const Range = ({
  value = 0,
  min = 0,
  max = 100,
  step = 1,
  isPercentage = true,
  className = '',
  onChange = () => {}
}: {
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  isPercentage?: boolean;
  onChange?: (value: number) => void;
}) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [bubblePosition, setBubblePosition] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (inputRef?.current?.offsetWidth && bubbleRef?.current?.offsetWidth) {
      let bubblePos = calculateBubblePosition(
        currentValue,
        min,
        max,
        inputRef.current.offsetWidth,
        bubbleRef.current.offsetWidth
      );
      setBubblePosition(bubblePos);
    }
  }, [
    currentValue,
    min,
    max,
    inputRef?.current?.offsetWidth,
    bubbleRef?.current?.offsetWidth,
    window
  ]);

  useLayoutEffect(() => {
    function updateBubble() {
      if (inputRef?.current?.offsetWidth && bubbleRef?.current?.offsetWidth) {
        let bubblePos = calculateBubblePosition(
          currentValue,
          min,
          max,
          inputRef.current.offsetWidth,
          bubbleRef.current.offsetWidth
        );
        setBubblePosition(bubblePos);
      }
    }
    window.addEventListener('resize', updateBubble);
    updateBubble();
    return () => window.removeEventListener('resize', updateBubble);
  }, []);

  const onRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(parseInt(e.target.value));
    onChange(parseInt(e.target.value));
  };

  return (
    <div className='flex flex-col'>
      <div className='py-sm cursor-pointer'>
        <div>
          <div
            style={{
              marginLeft: `${bubblePosition}px`
            }}
          >
            <div
              ref={bubbleRef}
              className={classNames(
                'P4M px-sm py-xs rounded-lg border-[1px] w-fit min-w-max cursor-pointer',
                setupForDark('text-secondary', 'border-text-input-default', 'bg-label-primary')
              )}
            >
              {`${currentValue}${isPercentage ? ' %' : ''}`}
            </div>
          </div>
        </div>
        <input
          className={classNames(
            className,
            'w-full cursor-pointer',
            'appearance-none rounded-full [&::-webkit-slider-runnable-track]:h-[8px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:-my-[4px] [&::-webkit-slider-thumb]:h-md [&::-webkit-slider-thumb]:w-md [&::-webkit-slider-thumb]:rounded-full ',
            setupForDark('bg-input-tertiary', '[&::-webkit-slider-thumb]:bg-icon-quaternary')
          )}
          type='range'
          value={currentValue}
          min={min}
          max={max}
          step={step}
          ref={inputRef}
          onChange={onRangeChange}
        />
      </div>
      <div className='flex flex-row justify-between'>
        <div
          className={classNames('P2R', setupForDark('text-secondary', 'border-text-input-default'))}
        >{`${min}${isPercentage ? ' %' : ''}`}</div>
        <div
          className={classNames('P2R', setupForDark('text-secondary', 'border-text-input-default'))}
        >{`${max}${isPercentage ? ' %' : ''}`}</div>
      </div>
    </div>
  );
};

export default Range;
